import { useState, useEffect, useCallback } from 'react';
import type { ReferralDashboard, ReferralHistoryItem } from '../types';
import { fetchReferralDashboard, fetchReferralHistory } from '../services/referral-api';

interface UseReferralsReturn {
  dashboard: ReferralDashboard | null;
  history: ReferralHistoryItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useReferrals(): UseReferralsReturn {
  const [dashboard, setDashboard] = useState<ReferralDashboard | null>(null);
  const [history, setHistory] = useState<ReferralHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [dashboardData, historyData] = await Promise.all([
        fetchReferralDashboard(),
        fetchReferralHistory(),
      ]);
      setDashboard(dashboardData);
      setHistory(historyData.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load referral data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { dashboard, history, isLoading, error, refetch };
}
